import React, { useState, useEffect } from 'react';
import type { ReadinessInput } from '../types';
import { useSunoReadiness } from '../useSunoReadiness';
import { ReadinessBadge } from './ReadinessBadge';
import { ScoreBreakdown } from './ScoreBreakdown';
import { TermHighlighter } from './TermHighlighter';
import { DeepAnalysisDisplay } from './DeepAnalysisDisplay';
import { Button, LoadingSpinner } from '@/components/ui';
import { ChevronDown, ChevronRight, Sparkles, AlertCircle } from 'lucide-react';

interface SunoReadinessPanelProps {
  styleText: string;
  lyricsText: string;
  blends: string[];
}

export function SunoReadinessPanel({ styleText, lyricsText, blends }: SunoReadinessPanelProps): React.ReactElement | null {
  const { result, deepAnalysis, isAnalyzing, deepError, analyze, requestDeepAnalysis, clear } = useSunoReadiness();
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!styleText.trim()) {
      clear();
      return;
    }
    const input: ReadinessInput = { styleText, lyricsText, blends };
    analyze(input);
  }, [styleText, lyricsText, blends, analyze, clear]);

  if (!result) return null;

  const input: ReadinessInput = { styleText, lyricsText, blends };

  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-2 hover:text-slate-300 transition-colors"
          >
            {expanded ? <ChevronDown size={14} className="text-slate-500" /> : <ChevronRight size={14} className="text-slate-500" />}
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Suno Readiness</span>
          </button>
          <ReadinessBadge
            score={result.totalScore}
            grade={result.grade}
            wordCount={result.wordCount}
            onClick={() => setExpanded(!expanded)}
          />
        </div>

        {expanded && (
          <div className="flex items-center gap-2">
            {isAnalyzing ? (
              <div className="flex items-center gap-2 text-purple-400">
                <LoadingSpinner size="xs" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Analyzing...</span>
              </div>
            ) : !deepAnalysis ? (
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Sparkles size={12} />}
                onClick={() => requestDeepAnalysis(input)}
                className="bg-purple-500/10 text-purple-400 border border-purple-500/20 hover:bg-purple-500/20 text-[10px] uppercase tracking-wider"
              >
                Deep Analysis
              </Button>
            ) : null}
          </div>
        )}
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          <ScoreBreakdown categories={result.categories} />

          <TermHighlighter
            strongTerms={result.strongTermsFound}
            deadWeightTerms={result.deadWeightTermsFound}
            conflictingGenres={result.conflictingGenresFound}
            conflictingVocals={result.conflictingVocalsFound}
          />

          {deepError && (
            <div className="flex items-start gap-2 p-3 bg-red-950/30 border border-red-500/20 rounded-lg">
              <AlertCircle size={14} className="text-red-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-red-400">{deepError}</p>
            </div>
          )}

          {deepAnalysis && <DeepAnalysisDisplay analysis={deepAnalysis} />}
        </div>
      )}
    </div>
  );
}
